import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Sparkles, ArrowRight, X, CheckCircle2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogPortal,
  DialogOverlay,
} from "@/components/ui/dialog";
import { usePlanFeatures, PlatformFeature, FEATURE_META, getUpgradeCtaLabel } from "@/hooks/usePlanFeatures";
import { cn } from "@/lib/utils";
import { ASK_ADMIN_FOR_PLAN, useCanManageBilling } from "@/lib/workspacePermissions";

// ──────────────────────────────────────────────────────────────────────
// PlanGate — wraps any element and restricts it for free-plan users
// ──────────────────────────────────────────────────────────────────────

interface PlanGateProps {
  /** The feature key to check access for */
  feature: PlatformFeature;
  /**
   * How to render the restricted state:
   *   "tooltip"  — shows the child with a lock icon + tooltip on hover
   *   "overlay"  — shows the child dimmed with a centered lock badge inline
   *   "hide"     — hides the child entirely when restricted
   */
  mode?: "tooltip" | "overlay" | "hide";
  /** If true, clicking/triggering the restricted element opens the upgrade modal */
  showModalOnClick?: boolean;
  /** Extra class applied to the wrapper div */
  className?: string;
  /** Custom positioning for the lock badge in tooltip mode */
  lockPosition?: "right" | "top-right" | "inline" | "none";
  children: React.ReactNode;
}

export function PlanGate({
  feature,
  mode = "tooltip",
  showModalOnClick = true,
  className = "",
  lockPosition = "right",
  children,
}: PlanGateProps) {
  const { isRestricted } = usePlanFeatures();
  const restricted = isRestricted(feature);

  // If user has access, render children as-is
  if (!restricted) return <>{children}</>;

  if (mode === "hide") return null;

  if (mode === "tooltip") {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <UpgradeWrapper
              feature={feature}
              showModalOnClick={showModalOnClick}
              className={className}
              lockPosition={lockPosition}
            >
              {children}
            </UpgradeWrapper>
          </TooltipTrigger>
          <TooltipContent side="right" align="center" className="z-[10030] max-w-[220px] border-none bg-ink-blue-900 text-white shadow-xl">
            <UpgradeTooltipBody feature={feature} />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // mode === "overlay"
  return (
    <div className={cn("relative inline-flex group items-center", className)}>
      {/* Dimmed children */}
      <div className="opacity-40 pointer-events-none select-none grayscale-[0.5]">{children}</div>
      {/* Lock badge centered over the element */}
      <UpgradeBadge
        feature={feature}
        showModalOnClick={showModalOnClick}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Internal pieces
// ──────────────────────────────────────────────────────────────────────

/** Wrapper that intercepts clicks and prevents default on restricted features */
function UpgradeWrapper({
  feature,
  showModalOnClick,
  className,
  lockPosition,
  children,
}: {
  feature: PlatformFeature;
  showModalOnClick: boolean;
  className: string;
  lockPosition: "right" | "top-right" | "inline" | "none";
  children: React.ReactNode;
}) {
  const [modalOpen, setModalOpen] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (showModalOnClick) setModalOpen(true);
  };

  return (
    <>
      <div
        onClick={handleClick}
        className={cn(
          "relative cursor-pointer group/gate transition-all duration-200",
          lockPosition === "inline" ? "inline-flex items-center" : "block",
          className
        )}
        role="button"
        aria-disabled="true"
      >
        {/* Dim children slightly but keep readable, block interaction */}
        <div className="opacity-60 pointer-events-none select-none transition-opacity group-hover/gate:opacity-80">
          {children}
        </div>

        {/* Lock badge */}
        {lockPosition !== "none" && (
          <span className={cn(
            "z-10 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-soft text-muted-foreground border border-line shadow-sm group-hover/gate:bg-gradient-to-br group-hover/gate:from-sunset-coral-400 group-hover/gate:to-soft-lavender-500 group-hover/gate:text-white group-hover/gate:border-transparent transition-all duration-300",
            lockPosition === "right" && "absolute right-2 top-1/2 -translate-y-1/2",
            lockPosition === "top-right" && "absolute -top-1 -right-1",
            lockPosition === "inline" && "ml-1.5"
          )}>
            <Lock className="h-2 w-2" />
          </span>
        )}
      </div>
      <UpgradeModal 
        feature={feature} 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)} 
      />
    </>
  );
}

/** Small centered badge for overlay mode */
function UpgradeBadge({
  feature,
  showModalOnClick,
}: {
  feature: PlatformFeature;
  showModalOnClick: boolean;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => { if (showModalOnClick) setModalOpen(true); }}
        className="absolute inset-0 flex items-center justify-center bg-white/10 backdrop-blur-[1px] hover:bg-white/20 transition-colors rounded-lg"
        aria-label="Upgrade to access this feature"
      >
        <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-surface border border-line text-foreground text-[11px] font-bold hover:scale-105 transition-transform">
          <Lock className="h-3 w-3 text-plum" /> Upgrade
        </span>
      </button>
      <UpgradeModal 
        feature={feature} 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)} 
      />
    </>
  );
}

/** Body rendered inside the Radix tooltip */
function UpgradeTooltipBody({ feature }: { feature: PlatformFeature }) {
  const meta = FEATURE_META[feature];
  return (
    <div className="flex flex-col items-start gap-0.5 py-0.5 px-1">
      <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-plum">
        <Sparkles className="h-3 w-3" />
        Pro Feature
      </span>
      <p className="text-[12px] font-medium leading-tight">
        {meta.title}
      </p>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// UpgradeModal — rich modal shown when clicking a restricted feature
// ──────────────────────────────────────────────────────────────────────

interface UpgradeModalProps {
  feature: PlatformFeature;
  isOpen: boolean;
  onClose: () => void;
}

export function UpgradeModal({ feature, isOpen, onClose }: UpgradeModalProps) {
  const navigate = useNavigate();
  const { isExpired } = usePlanFeatures();
  const meta = FEATURE_META[feature];
  const ctaLabel = getUpgradeCtaLabel(isExpired);
  const canManageBilling = useCanManageBilling();

  const handleUpgrade = () => {
    onClose();
    navigate("/billing/plans");
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogPortal>
        <DialogOverlay className="z-[99999] bg-black/40 backdrop-blur-sm" />
        <DialogContent className="z-[100000] w-[95%] max-w-[400px] border-none bg-white dark:bg-card p-0 shadow-2xl rounded-2xl overflow-hidden focus:outline-none">
          {/* Top visual brand bar */}
          <div className="h-1 bg-gradient-to-r from-sunset-coral-500 via-soft-lavender-500 to-sunset-coral-400" />

          <div className="p-7">
            {/* Header section */}
            <div className="flex flex-col items-center text-center mb-6">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-soft text-plum ring-1 ring-line">
                <Lock className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-foreground tracking-tight">
                {meta.title}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-[280px]">
                {meta.description}
              </p>
            </div>

            {/* Feature preview / context */}
            <div className="mb-8 space-y-3 bg-soft rounded-xl p-4 border border-line">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest text-center mb-1">
                Included in Pro Plans
              </p>
              {PAID_PLAN_HIGHLIGHTS.slice(0, 4).map((item) => (
                <div key={item} className="flex items-start gap-2.5 text-xs text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 dark:text-emerald-400 shrink-0 mt-0.5" />
                  <span className="font-medium">{item}</span>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              {!canManageBilling && (
                <p data-testid="upgrade-ask-admin" className="text-center text-xs text-muted-foreground">{ASK_ADMIN_FOR_PLAN}</p>
              )}
              {canManageBilling && <button
                onClick={handleUpgrade}
                className="group relative w-full overflow-hidden rounded-xl bg-ink-blue-900 dark:bg-white/10 px-6 py-3.5 text-sm font-bold text-white transition-all hover:bg-ink-blue-950 dark:hover:bg-white/20 active:scale-[0.98] shadow-lg"
              >
                <div className="flex items-center justify-center gap-2">
                  {ctaLabel}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </button>}

              <button
                onClick={onClose}
                className="w-full py-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close modal"
              >
                Maybe later
              </button>
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}

// What paid plans unlock globally (shown in the modal)
const PAID_PLAN_HIGHLIGHTS = [
  "Custom Domains - Host on your own brand",
  "Publish & host apps publicly",
  "GitHub repo sync & branch workflows",
  "Private projects & team workspaces",
  "Custom domains for your apps",
];
